import React, { useState, useEffect, useMemo } from 'react';
import createCar from '../../../api/cars/createCar';
import getFactoryList from '../../../api/cars/getFactoryList';
import { useNavigate } from 'react-router-dom';

import Button from '../../../components/Button/Button';
import Loader from '../../../components/Loader/Loader';
import alert from '../../../helpers/alert';
import getClients from '../../../api/clients/getClients';
import HookForm from '../../../components/HookForm/HookForm';
import Select from '../../../components/Select/Select';
import CarSellerForm from './components/CarSellerForm';
import RequisitesForm from './components/RequisitesForm';
import DocumentsForm from './components/DocumentsForm';
import CarBuyerForm from './components/BuyerForm';
import { createBuyer } from '../../../api/cars/createBuyer';
import { createSeller } from '../../../api/cars/createSeller';
import { uploadCarDocument } from '../../../api/cars/uploadCarDocument';
import {
  CAR_DOC_TYPE_PURCHASE_CONTRACT,
  CAR_DOC_TYPE_INSPECTION_ACT,
  CAR_DOC_TYPE_PHOTOS,
  CAR_DOCUMENT_TYPE_MAP,
} from '../../../constants/carDocumentTypes';
// constants
import { CAR_ORIGIN_TYPES, CAR_ORIGIN_LABELS, PURCHASE_PROCESS_TYPES, USED_CAR_FACTORY_ID } from '../../../constants/carOrigin';
import sortFactoryList from '../../../utils/sortFactoryList';
import { allowedOriginOptions, managersAddDictionaries, tenant } from '../../../brand/tenant';
import { buildCarInfoInputs } from './carInfoInputs';
import ConsignmentCreate from './Consignment/ConsignmentCreate';
import getContainers from '../../../api/cars/getContainers';
import ensureContainer from '../../../api/cars/ensureContainer';
import dayjs from 'dayjs';

const formStyles = {
  dealInfo: { display: 'grid', gap: '20px', gridTemplateColumns: '1fr 1fr 1fr 1fr', alignItems: 'flex-end' },
  completeSet: { display: 'grid', gap: '20px', gridTemplateColumns: '1fr 1fr' },
  other: { display: 'grid', gap: '20px', gridTemplateColumns: '1fr' },
};

const steps = [
  { key: 'car', label: 'Данные автомобиля' },
  { key: 'seller', label: 'Данные продавца' },
  { key: 'buyer', label: 'Данные покупателя' },
  { key: 'requisites', label: 'Реквизиты' },
  { key: 'documents', label: 'Документы' },
];

const CarCreate = ({ mode }) => {
  // mode: 'new' — форма новых авто (только заводские/ввезённые происхождения),
  //       'used' — форма б/у авто (только 4 типа выкупа),
  //       undefined — старое поведение (все типы в одном списке).
  // У брокера только с БУ-авто (JetQ) любая форма создания — БУ: на общую
  // /cars/create ведёт кнопка «Добавить автомобиль» из общего списка, и без
  // этого там всплывали новые и заводские варианты происхождения.
  const effectiveMode = tenant?.usedCarsOnly ? 'used' : mode;
  const isUsedMode = effectiveMode === 'used';
  const isNewMode = effectiveMode === 'new';
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [carOrigin, setCarOrigin] = useState('');
  const [carData, setCarData] = useState({});
  const [sellerData, setSellerData] = useState(null);
  const [buyerData, setBuyerData] = useState(null);
  const [requisitesData, setRequisitesData] = useState(null);
  const [documentsData, setDocumentsData] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [watchValues, setWatchValues] = useState({});
  const [file, setFile] = React.useState({});
  const [carId, setCarId] = useState(null);
  const [factoryList, setFactoryList] = useState([]);
  const [factoryId, setFactoryId] = useState(null);
  const [loadingFactoryList, setLoadingFactoryList] = useState(false);

  const [completeSet, setCompleteSet] = useState([
    { type: 'date', value: undefined, label: 'Дата прибытия в дилерский центр', placeholder: 'дд-мм-гггг' },
    {
      type: 'input',
      value: undefined,
      label: 'Скидка дилерского центра',
      placeholder: 'Укажите скидку в процентах',
      inputType: 'number',
    },
  ]);

  const [other, setOther] = useState([
    {
      type: 'select',
      value: undefined,
      label: 'Ответственный менеджер',
      placeholder: 'Выберите из списка',
      options: [
        { value: 1, label: 'Бахытжан Шагабутдинов' },
        { value: 2, label: 'Бахытжан Шагабутдинов' },
      ],
    },
  ]);
  const [containerId, setContainerId] = useState(null);

  const loadFactoryList = async () => {
    setLoadingFactoryList(true);
    try {
      const response = await getFactoryList();
      if (response?.data?.data) {
        setFactoryList(sortFactoryList(response.data.data));
      }
    } catch (error) {
      console.error('Ошибка загрузки списка заводов:', error);
      alert.error('Не удалось загрузить список заводов');
    } finally {
      setLoadingFactoryList(false);
    }
  };

  const originOptions = useMemo(() => {
    // Не-заводские типы происхождения. В режиме «БУ авто» оставляем только выкупные
    // (Выкуп у клиента / залогового / мена / поручение), в режиме «Новые авто» —
    // только не-выкупные (напр. «Новое авто, ввезённое»). Без режима — все, как раньше.
    const baseOptions = Object.keys(CAR_ORIGIN_TYPES)
      .filter((key) => CAR_ORIGIN_TYPES[key] !== CAR_ORIGIN_TYPES.NEW_KZ)
      .filter((key) => {
        const isPurchase = PURCHASE_PROCESS_TYPES.includes(CAR_ORIGIN_TYPES[key]);
        if (isUsedMode) return isPurchase;
        if (isNewMode) return !isPurchase;
        return true;
      })
      .map((key) => ({
        value: CAR_ORIGIN_TYPES[key],
        label: CAR_ORIGIN_LABELS[CAR_ORIGIN_TYPES[key]],
      }));

    // В режиме «БУ авто» заводские (новые) варианты не показываем вовсе.
    if (isUsedMode) {
      // У брокера со своим списком (JetQ — только договор поручения) остаются лишь они.
      return allowedOriginOptions(baseOptions);
    }

    if (factoryList.length > 0) {
      const factoryOptions = factoryList
        // Завод б/у-авто не место в списке новых.
        .filter((factory) => factory.id !== USED_CAR_FACTORY_ID)
        .map((factory) => ({
          value: `factory_${factory.id}`,
          label: 'Новое авто, ' + factory.name,
          factoryId: factory.id,
        }));
      return [...factoryOptions, ...baseOptions];
    } else {
      return [
        {
          value: CAR_ORIGIN_TYPES.NEW_KZ,
          label: CAR_ORIGIN_LABELS[CAR_ORIGIN_TYPES.NEW_KZ],
        },
        ...baseOptions,
      ];
    }
  }, [factoryList, isUsedMode, isNewMode]);

  const resolveContainerId = async ({ markOption, modelOption }) => {
    if (!markOption?.label || !modelOption?.label) {
      setContainerId(null);
      return;
    }

    try {
      // Серый дилер заводит марки и модели сам, и пары «марка + модель» для них
      // ещё нет — поиск по справочнику вернул бы пусто, а без неё не завести
      // комплектацию: селект просит выбрать марку и модель, хотя они выбраны.
      // Поэтому пару создаём, как это делает договор поручения.
      if (managersAddDictionaries() && markOption?.value && modelOption?.value) {
        const res = await ensureContainer({ markId: markOption.value, modelId: modelOption.value });
        setContainerId(res?.data?.id ?? null);
        return;
      }

      const query = `${markOption.label} ${modelOption.label}`;
      const res = await getContainers({ markAndModel: query, page: 1 });
      const container = res?.data?.containers?.[0];
      setContainerId(container?.id ?? null);
    } catch (e) {
      console.error('Ошибка определения containerId по марке/модели', e);
      setContainerId(null);
    }
  };

  // Поле «Происхождение» — единственное, что различается между экранами:
  // список опций зависит от режима (новые / б/у). Остальные характеристики общие
  // и живут в buildCarInfoInputs.
  const originInput = {
    component: Select,
    name: 'origin',
    label: 'Происхождение автомобиля',
    placeholder: 'Выберите тип происхождения',
    options: originOptions,
    onChangeCustom: (selectedOption) => {
      const newValue = selectedOption?.value;
      const prevOrigin = carOrigin;

      if (newValue !== prevOrigin && newValue) {
        if (typeof newValue === 'string' && newValue.startsWith('factory_')) {
          const selectedFactoryId = selectedOption.factoryId;
          setCarOrigin(CAR_ORIGIN_TYPES.NEW_KZ);
          setFactoryId(selectedFactoryId);
        } else {
          setCarOrigin(newValue);
          setFactoryId(null);
        }
        setCarData((prev) => ({ ...prev, origin: selectedOption }));

        setCurrentStep(0);
        setSellerData(null);
        setBuyerData(null);
        setRequisitesData(null);
        setDocumentsData(null);
        setContractData(null);
      }
    },
    rules: { required: true },
    defaultValue: carData?.origin,
  };

  const carInfo = [
    originInput,
    ...buildCarInfoInputs({
      carData,
      onMarkChange: () => setContainerId(null),
      onModelChange: (modelOption, markOption) => resolveContainerId({ markOption, modelOption }),
      getContainerId: () => containerId,
      // Серый дилер заводит марки и модели сам — у него нет закреплённых марок.
      creatableDictionaries: managersAddDictionaries(),
    }),
  ];

  useEffect(() => {
    getClients({ roleId: 2 }).then((res) => {
      const managers = res.data.users.map((item) => {
        return { value: item.id, label: item.name + ' ' + item.surname };
      });
      setOther([
        {
          type: 'select',
          value: undefined,
          label: 'Ответственный менеджер',
          placeholder: 'Выберите из списка',
          options: managers,
        },
      ]);
    });
    loadFactoryList();
  }, []);

  const validateForm = (docsData = documentsData) => {
    if (PURCHASE_PROCESS_TYPES.includes(carOrigin)) {
      if (!sellerData) {
        alert.error('Необходимо заполнить данные продавца');
        return false;
      }
      if (!buyerData) {
        alert.error('Необходимо заполнить данные покупателя');
        return false;
      }
      if (!requisitesData) {
        alert.error('Необходимо заполнить реквизиты');
        return false;
      }
      if (!docsData) {
        alert.error('Необходимо загрузить все документы');
        return false;
      }
    }

    return true;
  };

  const handleSellerCreated = (sellerFormData) => {
    setSellerData(sellerFormData);
    console.log('Seller data saved:', sellerFormData);
    setCurrentStep(currentStep + 1);
  };

  const handleBuyerSubmit = (data) => {
    setBuyerData(data);
    console.log('Buyer data saved:', data);
    setCurrentStep(currentStep + 1);
  };

  const handleRequisitesSubmit = (data) => {
    setRequisitesData(data);
    console.log('Requisites data saved:', data);
    setCurrentStep(currentStep + 1);
  };

  const handleDocumentsSubmit = (data) => {
    setDocumentsData(data);
    console.log('Documents data saved:', data);
    createNewCar(data);
  };

  const getCarDataForContract = () => {
    const fieldByName = (name) => carInfo.find((f) => f?.name === name);

    return {
      make: fieldByName('mark')?.value?.label || 'Не указана',
      model: fieldByName('model')?.value?.label || 'Не указана',
      year: fieldByName('issueYear')?.value || 'Не указан',
      vin: fieldByName('vin')?.value || 'Не указан',
      color: fieldByName('color')?.value?.label || 'Не указан',
      engineVolume: fieldByName('engine_volume')?.value || 'Не указан',
      power: fieldByName('power')?.value || 'Не указана',
    };
  };

  const shouldShowPurchaseProcess = () => {
    return PURCHASE_PROCESS_TYPES.includes(carOrigin);
  };

  const showStepper = PURCHASE_PROCESS_TYPES.includes(carOrigin);

  const isStepCompleted = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return carOrigin && carInfo.some((field) => field.value);
      case 1:
        return sellerData;
      case 2:
        return buyerData;
      case 3:
        return requisitesData;
      case 4:
        return documentsData;
      default:
        return false;
    }
  };

  // Рендер stepper
  const renderStepper = () => (
    <div style={{ display: 'flex', alignItems: 'center', margin: '0 20px' }}>
      {steps.map((step, idx) => {
        const isCompleted = isStepCompleted(idx);
        const isCurrent = idx === currentStep;

        return (
          <React.Fragment key={step.key}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: 120,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: isCompleted ? 'var(--brand-primary)' : isCurrent ? 'var(--brand-primary)' : 'var(--brand-row)',
                  color: isCompleted || isCurrent ? '#fff' : 'var(--brand-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 16,
                  marginBottom: 4,
                }}
              >
                {isCompleted ? '✓' : idx + 1}
              </div>
              <span
                style={{
                  fontSize: 13,
                  color: 'var(--brand-primary)',
                  textAlign: 'center',
                }}
              >
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  background: 'var(--brand-primary)',
                  margin: '0 8px',
                  marginTop: -10,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  const handleCarInfoSubmit = (values) => {
    setCarData(values);
    // setCarInfo((prev) => prev.map((item) => ({ ...item, value: values[item.name] })));
    if (!values?.vin || values?.vin?.trim()?.length != 17) {
      alert.error('Длина VIN должна быть 17 символов');
      return false;
    }

    if (!values?.issueYear || values?.issueYear > new Date().getFullYear() || values?.issueYear < 1900) {
      alert.error('Проверьте год выпуска авто');
      return false;
    }

    if (!values?.mark?.value || !values?.model?.value) {
      alert.error('Выберите марку и модель');
      return false;
    }

    if (!containerId) {
      alert.error('Не удалось определить контейнер по выбранной марке/модели');
      return false;
    }

    if (!carOrigin) {
      alert.error('Выберите происхождение автомобиля');
      return false;
    }
    setCurrentStep(currentStep + 1);
  };

  const validateAndCreateCar = async (values, onlyCreate) => {
    console.log('Car info submit:', values);
    if (!values?.vin || values?.vin?.trim()?.length != 17) {
      alert.error('Длина VIN должна быть 17 символов');
      return false;
    }

    if (!values?.issueYear || values?.issueYear > new Date().getFullYear() || values?.issueYear < 1900) {
      alert.error('Проверьте год выпуска авто');
      return false;
    }

    if (!carOrigin) {
      alert.error('Выберите происхождение автомобиля');
      return false;
    }

    // Завод спрашиваем, только когда есть из чего выбирать: у серых дилеров
    // (JetQ, MEGA) заводов нет вовсе, машина идёт от самого дилерского центра —
    // и на его счёт банк перечисляет деньги, как по б/у.
    if (carOrigin === CAR_ORIGIN_TYPES.NEW_KZ && !factoryId && factoryList.length > 0) {
      alert.error('Выберите завод');
      return false;
    }

    if (!values?.mark?.value || !values?.model?.value) {
      alert.error('Выберите марку и модель');
      return false;
    }

    if (!containerId) {
      alert.error('Не удалось определить контейнер по выбранной марке/модели');
      return false;
    }

    const originValue = values?.origin?.value;
    const isUsedFactory =
      typeof originValue === 'string' && originValue.startsWith('factory_')
        ? values?.isUsedFactory || factoryId === USED_CAR_FACTORY_ID
        : false;
    const isCarUsed =
      typeof originValue === 'string' && originValue.startsWith('factory_')
        ? isUsedFactory
        : PURCHASE_PROCESS_TYPES?.includes(originValue);

    const dcIds = []
      .concat(values?.dc || [])
      .map((dc) => dc?.value)
      .filter((value) => value != null);

    const finalOrigin =
      typeof originValue === 'string' && originValue.startsWith('factory_') ? CAR_ORIGIN_TYPES.NEW_KZ : originValue;

    const carPayload = {
      phid: 5,
      originCarId: finalOrigin,
      containerId,
      markId: values?.mark?.value,
      modelId: values?.model?.value,
      complectationId: values?.complectationId?.value,
      transmissionId: values?.transmissionId?.value,
      issueYear: values?.issueYear,
      vin: values?.vin?.trim(),
      color: values?.color?.value,
      price: parseInt(values?.price?.replace(/[^\d]/g, '')),
      engine_volume: values?.engine_volume,
      powerId: values?.power,
      wdId: values?.wdId?.value,
      cityId: values?.cityId?.value,
      dealerId: values?.dealerId?.value,
      mileage: values?.mileage ? parseInt(values?.mileage) : undefined,
      arrivalDate: values?.arrivalDate,
      saleForeDealer: values?.saleForeDealer,
      managerId: values?.managerId?.value,
      paid: 5124,
      paidTypeId: 1,
      countryId: 113,
      used: isCarUsed,
      ...(factoryId && { factoryId }),
      // Выбранный менеджером центр передаём только у серых дилеров (JetQ, MEGA):
      // у них марки за центрами не закреплены, и автоподбор на бэкенде отвечает
      // «у марки нет центра в этом городе» — даже когда центр выбран в форме.
      // У DOSCAR и Rox порядок прежний: центр подбирается по марке и городу.
      ...(managersAddDictionaries() && dcIds.length > 0 && { dcIds }),
    };
    console.log(carPayload);
    const data = new FormData();
    data.append('photo', file.photo);
    data.append('json', JSON.stringify(carPayload));

    const response = await createCar(data);

    if (onlyCreate) {
      alert.success('Автомобиль успешно создан');
      setTimeout(() => {
        navigate('/cars');
      }, 1000);
    }

    return response;
  };

  const renderStepContent = () => {
    if (!showStepper) {
      return (
        <HookForm
          inputs={carInfo}
          title={'Информация об автомобиле'}
          grid={formStyles.dealInfo}
          handlerSetState={(values) => {
            console.log('update', values);
            setCarData(values);
          }}
          onSubmit={(values) => validateAndCreateCar(values, true)}
          action={'Сохранить и продолжить'}
        />
      );
    }
    switch (steps[currentStep].key) {
      case 'car':
        return (
          <HookForm
            inputs={carInfo}
            title={'Информация об автомобиле'}
            grid={formStyles.dealInfo}
            handlerSetState={setCarData}
            onSubmit={handleCarInfoSubmit}
            action={'Далее'}
          />
        );
      case 'seller':
        return (
          <CarSellerForm
            onSellerCreated={handleSellerCreated}
            carData={getCarDataForContract()}
            currentData={sellerData}
          />
        );
      case 'buyer':
        return (
          <CarBuyerForm onBuyerCreated={handleBuyerSubmit} carData={getCarDataForContract()} currentData={buyerData} />
        );
      case 'requisites':
        return <RequisitesForm onSubmit={handleRequisitesSubmit} currentData={requisitesData} />;
      case 'documents':
        return <DocumentsForm onSubmit={handleDocumentsSubmit} currentData={documentsData} />;
      default:
        return null;
    }
  };

  const renderStepButtons = () => {
    return (
      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        {currentStep > 0 && <Button text={'Назад'} onClick={() => setCurrentStep(currentStep - 1)} />}
      </div>
    );
  };

  const createNewCar = async (currentDocsData = documentsData) => {
    if (!validateForm(currentDocsData)) return;
    setLoading(true);
    console.log('ALL DATA', carData, sellerData, buyerData, requisitesData, documentsData);
    try {
      let carIdLocal = carId;

      if (!carId) {
        const response = await validateAndCreateCar(carData);

        if (!response) return;
        carIdLocal = response.data?.car?.id || response.data?.id;
        setCarId(carIdLocal);
      }

      if (shouldShowPurchaseProcess() && sellerData) {
        console.log('Создание продавца...');

        try {
          await createSeller({
            ...sellerData,
            id: 0,
            CarId: carIdLocal,
            DOCUMENT_GIVED_DATE: dayjs(sellerData?.DOCUMENT_GIVED_DATE).format('DD.MM.YYYY'),
            bankAccount: requisitesData?.bankAccount,
            bankName: requisitesData?.bankName,
            bankBic: requisitesData?.bankBic,
          });
        } catch (error) {
          console.error('Ошибка создания продавца:', error);
          alert.error(error.message || 'Ошибка создания продавца');
        }
      }

      if (shouldShowPurchaseProcess() && buyerData) {
        console.log('Создание покупателя...');
        try {
          await createBuyer({
            CarId: carIdLocal,
            JuridicalName: buyerData.JuridicalName,
            JuridicalDirectorName: buyerData.JuridicalDirectorName,
            CompanyDetails: buyerData.CompanyDetails,
            ContractNumber: buyerData.ContractNumber,
            RepairWorkList: buyerData.RepairWorkList,
          });
        } catch (error) {
          console.error('Ошибка создания покупателя:', error);
          alert.error(error.message || 'Ошибка создания покупателя');
        }
      }

      const docsData = currentDocsData;
      if (docsData) {
        const docUploads = [
          {
            id: CAR_DOC_TYPE_PURCHASE_CONTRACT,
            file: docsData[CAR_DOC_TYPE_PURCHASE_CONTRACT] || docsData.purchaseContract,
          },
          {
            id: CAR_DOC_TYPE_INSPECTION_ACT,
            file: docsData[CAR_DOC_TYPE_INSPECTION_ACT] || docsData.inspectionAct,
          },
          { id: CAR_DOC_TYPE_PHOTOS, file: docsData[CAR_DOC_TYPE_PHOTOS] || docsData.photos },
        ];
        for (const { id, file } of docUploads) {
          if (file) {
            if (Array.isArray(file)) {
              for (const singleFile of file) {
                await uploadCarDocument({
                  file: singleFile,
                  carId: carIdLocal,
                  name: CAR_DOCUMENT_TYPE_MAP[id],
                  typeId: id,
                });
              }
            } else {
              await uploadCarDocument({
                file,
                carId: carIdLocal,
                name: CAR_DOCUMENT_TYPE_MAP[id],
                typeId: id,
              });
            }
          }
        }
      }

      alert.success('Данные успешно сохранены');
      setTimeout(() => {
        navigate(`/cars/car/${carIdLocal}/main-data`);
      }, 1000);
    } catch (error) {
      console.error('Ошибка создания автомобиля:', error);
      alert.error(error.message || 'Ошибка создания автомобиля');
    } finally {
      setLoading(false);
    }
  };

  // Договор поручения оформляется на одном экране, без степпера: там свои блоки
  // (ТС + техпаспорт, продавец-клиент, реквизиты ДЦ) и своё сохранение.
  if (carOrigin === CAR_ORIGIN_TYPES.CONSIGNMENT) {
    return (
      <div style={{ paddingBottom: '20px', marginTop: 20 }}>
        <ConsignmentCreate originInput={originInput} />
      </div>
    );
  }

  return (
    <>
      {loading && <Loader />}
      <div style={{ paddingBottom: '20px', marginTop: 20 }}>
        {/* <div className={styles.newClientDataPhoto}>
          <label>
            <input
              type="file"
              onChange={(event) => {
                setFile({ photo: event.target.files[0] });
              }}
            />
            <img alt="" />
            <p>Загрузить фото с пк</p>
            <span>Изображение в формате PNG или JPEG не более 200 Кб</span>
          </label>
        </div> */}
        {showStepper && renderStepper()}
        {renderStepContent()}
        {renderStepButtons()}
      </div>
    </>
  );
};

export default CarCreate;
